/* eslint-disable react-hooks/exhaustive-deps */
import { useState, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import dayjs from 'dayjs';
import MainPage from '../../MainPage';
import StickyHeader from '../Common/StickyHeader';
import useContacts from '../Hooks/useContacts';
import { useDispatch, useSelector } from 'react-redux';
import useStartVideoCall from '@/Components/Hooks/useStartVideoCall';
import { Actions, sessionType } from '@vantiva-sa/homesight_humaui/Utilities/Constants';
import { getContacts } from './Contacts';
import {
  toDateTimeByTimeZone,
  getTimeZoneFormat,
  formatEventTimes,
} from '@vantiva-sa/homesight_humaui/Utilities/DateTime';
import ContactSelectList from '../Common/ContactSelectList';
import {
  KnownActionsList,
  isActionAllowed,
} from '@vantiva-sa/homesight_humaui/Utilities/AccessControl';
import { getCurrentUser } from '@vantiva-sa/homesight_humaui/Store/authStore';
import { apiCallback } from '@vantiva-sa/homesight_humaui/Services/Common';
import { translator } from '@vantiva-sa/homesight_humaui/Store/configStore';
import { Divider, message, Tooltip } from 'antd';
import { isNull, isUndefined } from 'lodash';
import { OpenInNewIcon } from '../Common/Icons/OpenInNewIcon';
import { getPermissions } from '@vantiva-sa/homesight_humaui/Store/permisssionsStore';
import RecurringTaskModal from '../Task/RecurringTaskModal';
import NonRecurringTaskModal from '../Task/NonRecurringTaskModal';
import TaskModal from '../Task/TaskModal';
import { CallIcon, CloseIcon, EditIcon, SocialIcon } from '../Common/Icons';
import VisitIcon from '@/Components/Common/Icons/visit.svg';
import NewCustomButton from '../Common/NewCustomButton';
import { ButtonTypes } from '../Common/CustomButton';

const Mins_in_millis = 15 * 60 * 1000; // 15 minutes = 900000
const Hours_in_millis = 4 * 60 * 60 * 1000; // 4 hours = 14400000

const getFormattedEventDateTime = (eventTimestamp, timeZone, loginUserTimeformat) => {
  const trans = useSelector(translator);
  const dateTimeResult =
    loginUserTimeformat && loginUserTimeformat?.is24hoursformat
      ? toDateTimeByTimeZone(eventTimestamp, timeZone, loginUserTimeformat?.is24hoursformat)
      : toDateTimeByTimeZone(eventTimestamp, timeZone, loginUserTimeformat?.is24hoursformat);

  const originalFormat = dateTimeResult.format('MM/DD/YY');

  const date = timeZone ? dayjs(eventTimestamp).tz(timeZone) : dayjs(eventTimestamp);
  const currentDate = dayjs().tz(timeZone).startOf('day');

  let dayPrefix = date.format('dddd'); // Default to day name like "Wednesday, "
  if (date.isSame(currentDate, 'day')) {
    dayPrefix = 'Today';
  } else if (date.isSame(currentDate.add(1, 'day'), 'day')) {
    dayPrefix = 'Tomorrow';
  } else if (date.isSame(currentDate.subtract(1, 'day'), 'day')) {
    dayPrefix = 'Yesterday';
  }

  const translatedDayPrefix = trans(dayPrefix.toUpperCase()) + ', ';
  const translatedMonth = trans(date.format('MMMM').toUpperCase()) + ' ';
  const formattedDate = translatedDayPrefix + translatedMonth + date.format('D, YYYY');

  return {
    original: originalFormat,
    formatted: formattedDate,
  };
};

export const StartEvent = (props) => {
  const {
    onlyContent,
    parentBread,
    eventRecord,
    onActionClick,
    userHubsData,
    userInfo,
    rolename,
    hubRecord,
    openSubMenu = false,
    loginUserTimeformat,
    closeModalHandler,
    showModal,
    reFetchList,
    action,
    userTimezone,
    currentUserDataInfo,
  } = props;
  const userApi = useSelector((state) => state.apis.userApi);
  const timeZone = getTimeZoneFormat(userInfo.timezone);
  const dispatch = useDispatch();
  const { startCall } = useStartVideoCall();
  const trans = useSelector(translator);
  const permissions = useSelector(getPermissions());
  const { data: contacts, isFetching: fetchingContacts } = useContacts({
    useruuid: eventRecord?.useruuid,
  });
  const [invites, setInvites] = useState([]);
  const header = (parentBread || []).concat({ label: trans('EVENT') });
  const currentUser = useSelector(getCurrentUser);
  const loginUseruuid = useMemo(() => currentUser?.useruuid, [currentUser]);
  const isEventRecurring = useMemo(() => {
    return eventRecord?.repeat && ['daily', 'custom', 'weekly'].includes(eventRecord?.repeat);
  }, [eventRecord]);
  const [activeTab, setActiveTab] = useState('schedule');
  const [isJoinButtonVisible, setJoinButtonVisible] = useState(false);
  const [isEventModalVisible, setIsEventModalVisible] = useState(false);

  const handleEventClick = () => {
    setIsEventModalVisible(true);
  };
  const handleModalConfirm = () => {
    setIsEventModalVisible(false);
    modifyEventDetails();
  };
  const handleModalCancel = () => {
    setIsEventModalVisible(false);
  };

  const modifyEventDetails = () => {
    closeModalHandler(false);
    onActionClick(
      eventRecord?.event_type === sessionType.reminder
        ? Actions.editReminderEvent
        : Actions.editEvent,
      {
        ...eventRecord,
        userHubsData,
      }
    )();
  };
  const handleTabChange = (key) => {
    setActiveTab(key);
    onActionClick(null, null, key)();
  };

  const fetchData = (user) => {
    const errorCallback = (error) => {
      console.error('Error fetching user information:', error);
    };
    const successCallback = (data) => {
      setInvites((prevState) =>
        prevState.map((item) => {
          if (item.useruuid === data?.user?.useruuid) return data.user;
          else return item;
        })
      );
    };
    try {
      const { useruuid } = user;
      if (!useruuid) return;
      userApi.apiClient.callApi(
        '/hacloud/api/v1/user/{useruuid}/info',
        'GET',
        { useruuid },
        { device: true },
        { 'Cache-Control': 'no-cache' },
        {},
        null,
        [],
        [],
        ['application/json'],
        Object,
        null,
        apiCallback({
          translator: trans,
          failCallback: errorCallback,
          errorCallback,
          successCallback,
        })
      );
    } catch (error) {
      errorCallback(error);
    }
  };

  useEffect(() => {
    let invitedContacts = eventRecord?.invited_contacts;
    let filteredContacts = getContacts(invitedContacts, [
      ...(contacts ?? []),
      ...(userHubsData ?? []),
      ...(userInfo ? [userInfo] : []),
    ]);
    setInvites(filteredContacts);
    if (contacts?.length > 0) {
      filteredContacts.filter((item) => !('firstname' in item)).forEach((item) => fetchData(item));
    }
    setJoinButtonVisible(
      checkCallButtonVisibility(eventRecord?.event_timestamp, eventRecord?.invited_contacts) &&
        eventRecord?.event_type?.toLowerCase() !== sessionType?.zoom &&
        eventRecord?.event_type?.toLowerCase() !== sessionType?.reminder &&
        !eventRecord?.event_type?.toLowerCase() === 'home_visit'
    );
  }, [contacts, eventRecord]);

  const checkCallButtonVisibility = (datetime, invitedList) => {
    //Disabled call button if contact not exists in invited list.
    let contactExist = invitedList?.includes(loginUseruuid);
    if (!contactExist) {
      return false;
    }
    if (!datetime) return false;
    let currentTime = new Date().getTime();
    let eventTime = new Date(datetime).getTime();
    let beforeEvent = eventTime - currentTime;
    let afterEvent = currentTime - eventTime;
    //Display the meeting start button before 15 minutes from event starttime
    if (beforeEvent > 0 && beforeEvent <= Mins_in_millis) {
      return true;
    }
    // Display the meeting start button until 4 hours from event starttime
    if (afterEvent >= 0 && afterEvent <= Hours_in_millis) {
      return true;
    }
    return false;
  };

  const checkEditButtonVisibility = (event_originator_id) => {
    if (
      event_originator_id === loginUseruuid ||
      (rolename !== undefined &&
        isActionAllowed(rolename, KnownActionsList.manage_schedule, permissions))
    ) {
      return true;
    }
  };
  const EventCreatedDate = (dateString) => {
    const rearrangeDate = (dateString) => {
      const [year, month, day] = dateString.split('-');
      return `${month}.${day}.${year}`;
    };
    return rearrangeDate(dateString);
  };

  const FormattedReminderDuration = (duration) => {
    let reminderDuration = '';
    if (duration === -1) {
      reminderDuration = trans('NOREMINDER');
    } else if (duration === 0) {
      reminderDuration = trans('ATSTARTTIME');
    } else {
      reminderDuration = `${duration} ${trans('MINUTES_L')}`;
    }
    return reminderDuration;
  };

  const renderEventModal = () => {
    if (!isEventModalVisible) return null;
    return isEventRecurring ? (
      <RecurringTaskModal
        isModalOpen={isEventModalVisible}
        onConfirm={handleModalConfirm}
        onCancel={handleModalCancel}
        showDeleteMsg={true}
        modalHeader={`${trans('MODIFY_PERIODIC_L')} ${
          eventRecord?.event_type === 'reminder' || eventRecord?.event_type === 'home_visit'
            ? trans('REMINDER_L')
            : eventRecord?.event_type === 'tasks'
              ? trans('TASK_L')
              : trans('CALL_L')
        }`}
      />
    ) : (
      <NonRecurringTaskModal
        isModalOpen={isEventModalVisible}
        onConfirm={handleModalConfirm}
        onCancel={handleModalCancel}
        showDeleteMsg={false}
        modalHeader={`${trans('MODIFY')} ${
          eventRecord?.event_type === 'reminder' || eventRecord?.event_type === 'home_visit'
            ? trans('REMINDER_L')
            : trans('CALL_L')
        }`}
        modalType={
          eventRecord?.event_type === 'reminder' || eventRecord?.event_type === 'home_visit'
            ? trans('REMINDER_L')
            : trans('CALL_L')
        }
        actionType={'modify'}
      />
    );
  };
  const headerSection = (
    <div className="event-details-header">
      <div className="event-details-title">
        {eventRecord?.event_type === 'reminder' ? (
          <SocialIcon style={{ fill: '#005D98' }} />
        ) : eventRecord?.event_type === 'home_visit' ? (
          <VisitIcon style={{ fill: '#005D98' }} />
        ) : (
          <CallIcon style={{ fill: '#005D98' }} />
        )}
        <span>{eventRecord?.event_name}</span>
      </div>
      <div className="event-details-actions">
        {checkEditButtonVisibility(eventRecord?.event_originator_id) && hubRecord && (
          <Tooltip title={eventRecord?.groupuuid && trans('EDITDISABLEDFORGROUPEVENTS')}>
            <div
              className={`edit-action ${eventRecord?.groupuuid ? 'group-edit-action-disabled' : ''}`}
              onClick={() => {
                if (
                  !eventRecord?.groupuuid &&
                  checkEditButtonVisibility(eventRecord?.event_originator_id)
                ) {
                  handleEventClick();
                }
              }}
            >
              <EditIcon style={{ stroke: '#4E758E', height: '23px' }} />
            </div>
          </Tooltip>
        )}
        <CloseIcon
          style={{
            height: '15px',
            width: '20px',
            cursor: 'pointer',
            fill: '#4E758E',
            stroke: '#4E758E',
          }}
          onClick={closeModalHandler}
        />
      </div>
    </div>
  );

  const bodySection = (
    <>
      <div className="event-details-body">
        <div className="event-details">
          <span className="general-info-text">{trans('GENERALINFORMATION')}</span>
          <div className="events-details-info">
            <div className="event-scheduled-day-info">
              {
                getFormattedEventDateTime(
                  eventRecord?.event_timestamp,
                  timeZone,
                  loginUserTimeformat
                ).formatted
              }
              <div className="event-scheduled-daydata">
                <div className="event-scheduled-repeat-alert">
                  {trans('REPEATS:')}
                  <span className="event-scheduled-infovalue">
                    {eventRecord && eventRecord?.repeat
                      ? trans(String(eventRecord?.repeat).trim().toUpperCase())
                      : trans('DOESNOT_REPEAT')}
                  </span>
                </div>
                <div className="event-scheduled-repeat-alert">
                  {trans('ALERT:')}
                  <span className="event-scheduled-infovalue">
                    {!isNull(eventRecord?.reminder_minutes) &&
                      !isUndefined(eventRecord?.reminder_minutes) &&
                      FormattedReminderDuration(eventRecord?.reminder_minutes)}
                  </span>
                </div>
              </div>
              {eventRecord?.create_time && eventRecord?.create_by && (
                <div className="event-created-date">
                  {trans('CREATEDON')} {EventCreatedDate(eventRecord?.create_time)} {trans('BY_L')}{' '}
                  {eventRecord?.create_by}
                </div>
              )}
            </div>
            <div
              className={`event-scheduled-date-info ${
                isJoinButtonVisible ? 'join-call-exists' : 'no-join-call'
              }`}
            >
              <div className="time-text">
                {eventRecord?.event_type !== 'tasks' &&
                  formatEventTimes(
                    eventRecord?.event_startdate,
                    eventRecord?.event_starttime,
                    eventRecord?.event_startdate,
                    eventRecord?.event_endtime,
                    userTimezone,
                    loginUserTimeformat?.is24hoursformat
                  )}
              </div>
              {isJoinButtonVisible && (
                <NewCustomButton
                  type={ButtonTypes.primary}
                  label={trans('JOIN')}
                  icon={<CallIcon className="update-icon-enabled" />}
                  onClick={() =>
                    startCall({
                      meeting_id: eventRecord?.schedule_eventuuid,
                      eventInfo: eventRecord,
                    })
                  }
                />
              )}
            </div>
          </div>
          <div className="event-description">
            <div className="label">{trans('DESCRIPTION')}</div>
            <div className="text description">{eventRecord?.event_description}</div>
            {eventRecord?.meeting_url && (
              <>
                <div className="label">{trans('LINK')}</div>
                <div className="url-link">
                  <a target="_blank" rel="noreferrer" href={eventRecord?.meeting_url}>
                    {eventRecord?.meeting_url}
                    <OpenInNewIcon className="custom-icon" />
                  </a>
                </div>
              </>
            )}
          </div>
        </div>
        <ContactSelectList
          displayOnlySelected={true}
          fetchingContacts={fetchingContacts}
          selectedContactList={invites}
          selectedListHeader={trans('PARTICIPANTS')}
          selectionListHeader={trans('ADDCONTACTS')}
          selectionContactList={[]}
          showRemoveIcon={false}
          showRelationship={false}
          hubRecord={hubRecord}
          starteventAction={false}
          trans={trans}
        />
      </div>
    </>
  );

  const render = (
    <div className="startevent-page-wrapper flex-column" style={{ width: '100%', height: '100%' }}>
      {renderEventModal()}
      <StickyHeader>{headerSection}</StickyHeader>
      <Divider style={{ margin: '0px', borderColor: '#C9C9C9' }} />
      {bodySection}
    </div>
  );

  return onlyContent && action === Actions.startEvent ? (
    <TaskModal showModal={showModal} closeModalHandler={closeModalHandler}>
      {render}
    </TaskModal>
  ) : (
    <MainPage
      header={header}
      hasSider={true}
      activeKey={activeTab}
      openSubMenu={openSubMenu}
      onChange={handleTabChange}
      hubUserRole={hubRecord?.rolename}
      noModals={!!openSubMenu}
    >
      {render}
    </MainPage>
  );
};

StartEvent.propTypes = {
  action: PropTypes.string,
  onlyContent: PropTypes.bool,
  parentBread: PropTypes.arrayOf(PropTypes.object),
};

export default StartEvent;
